// services/appleAuth.ts — Apple Sign In JS SDK loader + helpers.
//
// Dynamically loads the Apple JS SDK and provides idempotent init + popup
// trigger functions.

declare global {
  interface AppleSignInAPI {
    auth: {
      init: (config: {
        clientId: string;
        scope: string;
        redirectURI: string;
        usePopup: boolean;
      }) => void;
      signIn: () => Promise<{
        authorization: {
          id_token: string;
          code?: string;
        };
        user?: {
          name?: {
            firstName?: string;
            lastName?: string;
          };
          email?: string;
        };
      }>;
    };
  }
  interface Window {
    AppleID?: AppleSignInAPI;
  }
}

let scriptLoaded = false;
let scriptLoading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptLoaded && window.AppleID) return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise<void>((resolve, reject) => {
    if (document.querySelector('script[src*="appleid.cdn-apple.com"]')) {
      scriptLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Apple Sign In SDK"));
    document.head.appendChild(script);
  });

  return scriptLoading;
}

export async function initAppleSignIn(
  clientId: string,
  redirectUri: string,
): Promise<void> {
  await loadScript();
  window.AppleID!.auth.init({
    clientId,
    scope: "name email",
    redirectURI: redirectUri,
    usePopup: true,
  });
}

/** Error thrown by signInWithApple — carries Apple's machine-readable code. */
export class AppleSignInError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.name = "AppleSignInError";
    this.code = code;
  }
}

/** Apple `error` codes that mean the user backed out — caller should no-op. */
export const APPLE_CANCEL_CODES = new Set([
  "popup_closed_by_user",
  "user_cancelled_authorize",
  "user_trigger_new_signin_flow",
]);

export async function signInWithApple(): Promise<{
  id_token: string;
  user?: { name?: { firstName?: string; lastName?: string } };
}> {
  if (!window.AppleID) {
    await loadScript();
  }
  let response: Awaited<ReturnType<AppleSignInAPI["auth"]["signIn"]>>;
  try {
    response = await window.AppleID!.auth.signIn();
  } catch (raw: unknown) {
    // Apple rejects with a plain object like `{ error: "popup_closed_by_user" }`
    // (NOT an Error), so stringifying it yields "[object Object]". Normalise to
    // an AppleSignInError carrying the real code so callers can branch on it.
    const code =
      raw && typeof raw === "object" && "error" in raw
        ? String((raw as { error: unknown }).error)
        : "apple_signin_failed";
    throw new AppleSignInError(code);
  }
  return {
    id_token: response.authorization.id_token,
    user: response.user ? { name: response.user.name } : undefined,
  };
}
